// src/registered/site-admin/report_management/blueprint/ConductReportingController.js
//
// State for the "Conduct ESG Reporting" screen:
// Lead order: "Entering data for" (company, MUST come first) → pillar (E / S / G)
// → that pillar's topics → the topic's form.
//   phase "pick"  — choose a pillar, then a reporting topic, or reopen a saved section
//   phase "form"  — a form generated from the topic's field definitions.
//
// Every value is saved to the ONE report store (sustina-reporting service,
// reportDataService) with its provenance: typed (MANUAL), from HR & Workforce
// (HR), from Accounts & Ledger (LEDGER) or from meter readings (METER). A section
// stays PRE_DRAFT however often it is saved, until the user sets it to DRAFT;
// after that each save needs a coded reason and makes a new version, and any
// version can be made current again.
//
// Also drives the smart-meter simulation: generate a batch of readings for the
// active company and pull period totals into the Energy / Water forms.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useCompanyContext } from "../../../common/company/CompanyContext";
import { useSecurity } from "../../../../auth/registered/SEC_03_03_SecurityContext";
import { fetchCorporateRegistry } from "../../../corporate_details/corporateInteractionService";
import { GoldenSourceHelper } from "../../../../app/Switch_Core_React_Standards";
import {
  REPORTING_TEMPLATES,
  PILLARS,
  templateByKey,
  emptyValues,
  missingRequired,
  fieldCount,
} from "./blueprintReportingTemplates";
import {
  fetchReportCodes,
  listSections,
  findSection,
  getSection,
  sectionHistory,
  saveSection,
  setSectionDraft,
  restoreSectionVersion,
  toStoredText,
  fromStoredText,
} from "./reportDataService";
import { flattenFields } from "./blueprintReportingTemplates";
import { fetchMetrics } from "../../../corporate_details/hr_sync/hrSyncInteractionService";
import { fetchFigures } from "../../../corporate_details/account_ledger/accountLedgerService";
import { figuresToTopicValues, figureByPath } from "../../../corporate_details/account_ledger/ledgerFiguresDTO";
import { metricsToTopicValues, metricByCode } from "../../../corporate_details/hr_sync/HrSyncDTO";
import {
  generateReadingBatch,
  latestBatch,
  rollupForPeriod,
} from "./meterSimulationService";

/** The default reporting period: the last full calendar year. */
function lastYearRange() {
  const y = new Date().getFullYear() - 1;
  return { start: `${y}-01-01`, end: `${y}-12-31` };
}

/** ISO date (yyyy-mm-dd) for the first/last day of last calendar month (meter simulation). */
function lastMonthRange() {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const last = new Date(now.getFullYear(), now.getMonth(), 0);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { start: iso(first), end: iso(last) };
}

export default function ConductReportingController() {
  const { activeCompanyId, activeCompanyName, setActiveCompany } = useCompanyContext();
  const { myCorporateEntityIds, isGlobalAdmin } = useSecurity();
  const siteKey = process.env.REACT_APP_SITE_KEY;

  const [phase, setPhase] = useState("pick");
  const [pillar, setPillar] = useState(null); // "E" | "S" | "G" once chosen

  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  const [submissions, setSubmissions] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listNotice, setListNotice] = useState("");

  const [topic, setTopic] = useState(null); // full template
  const [values, setValues] = useState({});
  const [sources, setSources] = useState({}); // path -> { sourceCode, sourceRef, enteredBy, enteredAt }
  const [status, setStatus] = useState("PRE_DRAFT");
  const [editingId, setEditingId] = useState(null); // SectionId once saved
  const [baseline, setBaseline] = useState(null); // { from, to, values } as last loaded / saved
  const [versions, setVersions] = useState([]);
  const [currentVersionNo, setCurrentVersionNo] = useState(null);
  const [draftedBy, setDraftedBy] = useState(null);
  const [history, setHistory] = useState([]);
  const [reasonCodes, setReasonCodes] = useState([]);
  const [reason, setReason] = useState({ code: "", note: "" });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [simBatch, setSimBatch] = useState(null);
  const [hrBusy, setHrBusy] = useState(false);
  const [ledgerBusy, setLedgerBusy] = useState(false);
  const [simBusy, setSimBusy] = useState(false);

  // --- saved-section list -------------------------------------------------

  const refreshList = useCallback(async () => {
    if (activeCompanyId == null) {
      setSubmissions([]);
      return;
    }
    setLoadingList(true);
    try {
      const rows = await listSections(activeCompanyId);
      setSubmissions(
        (rows || []).map((r) => ({
          id: r.sectionId,
          topicKey: r.topicKey,
          topicName: templateByKey(r.topicKey)?.name || r.topicKey,
          periodStart: r.periodFrom,
          periodEnd: r.periodTo,
          status: r.statusCode,
          version: r.currentVersionNo,
          valueCount: r.valueCount,
          updatedAt: r.updatedAt,
          updatedBy: r.updatedBy,
        })),
      );
      setListNotice("");
    } catch (err) {
      setListNotice(err?.message || "Could not load saved sections.");
    } finally {
      setLoadingList(false);
    }
  }, [activeCompanyId]);

  useEffect(() => {
    refreshList();
    setSimBatch(latestBatch(activeCompanyId));
  }, [refreshList, activeCompanyId]);

  useEffect(() => {
    fetchReportCodes()
      .then((codes) => setReasonCodes((codes || []).filter((c) => c.codeSet === "CHANGE_REASON" && !["INITIAL_DRAFT", "VERSION_RESTORED"].includes(c.code))))
      .catch(() => setReasonCodes([]));
  }, []);

  // --- company selector (this page is standalone, not under the hub) -----

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingCompanies(true);
      try {
        const registry = await fetchCorporateRegistry({ siteKey });
        const rows = (registry || [])
          .map((c) => ({
            id: String(GoldenSourceHelper.get(c, "CorporateEntityId")),
            name:
              GoldenSourceHelper.get(c, "EntityName") ||
              GoldenSourceHelper.get(c, "Name") ||
              `Company ${GoldenSourceHelper.get(c, "CorporateEntityId")}`,
          }))
          .filter((c) => c.id && c.id !== "null" && c.id !== "undefined");
        const mine = isGlobalAdmin
          ? rows
          : rows.filter((c) => myCorporateEntityIds.includes(c.id));
        if (alive)
          setCompanies(mine.sort((a, b) => a.name.localeCompare(b.name)));
      } catch {
        if (alive) setCompanies([]);
      } finally {
        if (alive) setLoadingCompanies(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [siteKey, isGlobalAdmin, myCorporateEntityIds]);

  const pickCompany = (id) => {
    if (!id) return;
    const picked = companies.find((c) => c.id === String(id));
    setActiveCompany(String(id), picked ? picked.name : null);
    setPillar(null);
  };

  const pickPillar = (key) => {
    setPillar(key);
    setError("");
    setNotice("");
  };

  const backToPillars = () => {
    backToPick();
    setPillar(null);
  };

  // --- open / start -----------------------------------------------------

  /** Load a stored section into the form (values, provenance, status, versions). */
  const loadSection = useCallback(async (t, sectionId) => {
    const sec = await getSection(sectionId);
    const types = Object.fromEntries(flattenFields(t).map((f) => [f.path, f.type]));
    const vals = { ...emptyValues(t) };
    const src = {};
    (sec.values || []).forEach((v) => {
      vals[v.path] = fromStoredText(v.value, types[v.path]);
      src[v.path] = { sourceCode: v.sourceCode, sourceRef: v.sourceRef, enteredBy: v.enteredBy, enteredAt: v.enteredAt };
    });
    if (!vals["evidence.period_start"]) vals["evidence.period_start"] = sec.periodFrom;
    if (!vals["evidence.period_end"]) vals["evidence.period_end"] = sec.periodTo;
    setValues(vals);
    setSources(src);
    setStatus(sec.statusCode);
    setEditingId(sec.sectionId);
    setVersions(sec.versions || []);
    setCurrentVersionNo(sec.currentVersionNo);
    setDraftedBy(sec.draftedBy ? { by: sec.draftedBy, at: sec.draftedAt } : null);
    setBaseline({ from: sec.periodFrom, to: sec.periodTo, values: JSON.parse(JSON.stringify(vals)) });
    setReason({ code: "", note: "" });
    setHistory(await sectionHistory(sec.sectionId).catch(() => []));
  }, []);

  const resetForm = (t) => {
    const blank = emptyValues(t);
    const range = lastYearRange();
    blank["evidence.period_start"] = range.start;
    blank["evidence.period_end"] = range.end;
    if (activeCompanyName) blank["evidence.boundary"] = activeCompanyName;
    setValues(blank);
    setSources({});
    setStatus("PRE_DRAFT");
    setEditingId(null);
    setVersions([]);
    setCurrentVersionNo(null);
    setDraftedBy(null);
    setHistory([]);
    setBaseline(null);
    setReason({ code: "", note: "" });
    return range;
  };

  /** Open a topic for the default period — the stored section if there is one. */
  const startTopic = async (key) => {
    const t = templateByKey(key);
    if (!t) return;
    setTopic(t);
    setError("");
    setNotice("");
    const range = resetForm(t);
    setPhase("form");
    try {
      const found = await findSection(activeCompanyId, t.key, range.start, range.end);
      if (found?.sectionId) {
        await loadSection(t, found.sectionId);
        setNotice(`Opened the stored ${t.name} section for ${range.start} → ${range.end}.`);
      }
    } catch (err) {
      setError(err?.message || "Could not reach the report store.");
    }
  };

  const openSubmission = async (summary) => {
    setError("");
    setNotice("");
    const t = templateByKey(summary.topicKey);
    if (!t) {
      setError(`Unknown topic ${summary.topicKey}.`);
      return;
    }
    setPillar(t.pillar);
    setTopic(t);
    resetForm(t);
    setPhase("form");
    try {
      await loadSection(t, summary.id);
    } catch (err) {
      setError(err?.message || "Could not open the section.");
    }
  };

  const backToPick = () => {
    setPhase("pick");
    setTopic(null);
    setValues({});
    setEditingId(null);
    setBaseline(null);
    setError("");
    refreshList();
  };

  // --- field editing ---------------------------------------------------
  // Typing a value makes its provenance MANUAL (the user who saves it).

  const setValue = (path, val) => {
    setValues((prev) => ({ ...prev, [path]: val }));
    setSources((prev) => ({ ...prev, [path]: { sourceCode: "MANUAL", sourceRef: null } }));
  };

  /** Values filled by a feed keep where they came from. */
  const applyFilled = (filled, sourceCode, refFor) => {
    setValues((prev) => ({ ...prev, ...filled }));
    setSources((prev) => {
      const next = { ...prev };
      Object.keys(filled).forEach((k) => { next[k] = { sourceCode, sourceRef: refFor(k) }; });
      return next;
    });
  };

  // --- smart-meter simulation ----------------------------------------

  const simulateMeters = async () => {
    setSimBusy(true);
    setError("");
    try {
      const range = lastMonthRange();
      const batch = await generateReadingBatch({
        companyId: activeCompanyId,
        companyName: activeCompanyName || "",
        periodStart: range.start,
        periodEnd: range.end,
      });
      setSimBatch(batch);
      setNotice(
        `Simulated ${batch.readingCount} readings from ${batch.meterPointCount} meters for ${range.start} → ${range.end}.`,
      );
    } catch (err) {
      setError(err?.message || "Could not simulate meter data.");
    } finally {
      setSimBusy(false);
    }
  };

  const canPullMeters = useMemo(() => {
    if (!topic) return false;
    return topic.key === "energy" || topic.key === "water";
  }, [topic]);

  const pullFromMeters = () => {
    if (!canPullMeters) return;
    const ps = values["evidence.period_start"] || simBatch?.periodStart;
    const pe = values["evidence.period_end"] || simBatch?.periodEnd;
    const { values: rolled, meta } = rollupForPeriod(activeCompanyId, ps, pe);

    // only apply fields that belong to this topic
    const applied = {};
    Object.entries(rolled).forEach(([path, amount]) => {
      if (path.startsWith(`${topic.key}.`)) applied[path] = amount;
    });

    if (Object.keys(applied).length === 0) {
      setNotice(
        simBatch
          ? "No meter readings fall inside this reporting period. Adjust the period or simulate again."
          : "No simulated meter data yet — use “Simulate meter data” first.",
      );
      return;
    }

    applyFilled(applied, "METER", () => `${meta.metersUsed} meter(s), ${ps} → ${pe}`);
    setValues((prev) => ({
      ...prev,
      "evidence.source_system":
        prev["evidence.source_system"] || "Simulated smart-meter feed",
      "evidence.data_quality": prev["evidence.data_quality"] || "MEASURED",
    }));
    setNotice(
      `Filled ${Object.keys(applied).length} field(s) from ${meta.metersUsed} meter(s): ${Object.keys(applied)
        .map((p) => p.split(".")[1])
        .join(", ")}.`,
    );
  };

  // --- HR & Workforce figures -----------------------------------------
  // Reads HR_SYNC.Metric for the form's evidence period (connected HR system or
  // hand-entered); falls back to the calendar year of the period's end.
  const fillFromHr = async () => {
    if (!topic?.hr || activeCompanyId == null) return;
    setHrBusy(true);
    setError("");
    setNotice("");
    try {
      const ps = values["evidence.period_start"];
      const pe = values["evidence.period_end"];
      const g = (o, k) => GoldenSourceHelper.get(o, k);
      const read = async (from, to) =>
        (await fetchMetrics(activeCompanyId, from, to)).map((m) => ({ metricCode: g(m, "MetricCode"), value: g(m, "Value"), source: g(m, "SourceCode"), tool: g(m, "ToolName") }));
      let rows = ps && pe ? await read(ps, pe) : [];
      let used = ps && pe ? `${ps} → ${pe}` : "";
      if (!rows.length && pe) {
        const y = String(pe).slice(0, 4);
        rows = await read(`${y}-01-01`, `${y}-12-31`);
        used = `calendar year ${y}`;
      }
      const filled = metricsToTopicValues(rows, topic.key);
      const keys = Object.keys(filled);
      if (!keys.length) {
        setNotice("No HR figures are held for this period yet — connect your HR system, map your codes or enter them on HR & Workforce.");
        return;
      }
      const labelFor = (k) => {
        const m = rows.find((r) => (metricByCode(r.metricCode)?.fields || []).includes(`${topic.key}.${k}`));
        return `HR & Workforce · ${metricByCode(m?.metricCode)?.label || k} (${used})`;
      };
      applyFilled(filled, "HR", labelFor);
      setValues((prev) => ({ ...prev, "evidence.source_system": prev["evidence.source_system"] || "HR & Workforce" }));
      const names = rows.filter((r) => keys.some((k) => (metricByCode(r.metricCode)?.fields || []).includes(`${topic.key}.${k}`))).map((r) => metricByCode(r.metricCode)?.label);
      setNotice(`Filled ${keys.length} field(s) from HR figures (${used}): ${[...new Set(names)].join(", ")}.`);
    } catch (err) {
      setError(err?.message || "Could not read the HR figures.");
    } finally {
      setHrBusy(false);
    }
  };

  // --- Accounts & Ledger figures --------------------------------------
  // Automated values from mapped account codes, or hand-entered ones (which win),
  // for the form's evidence period; falls back to the calendar year of its end.
  const fillFromLedger = async () => {
    if (!topic || topic.pillar !== "E" || activeCompanyId == null) return;
    setLedgerBusy(true);
    setError("");
    setNotice("");
    try {
      const ps = values["evidence.period_start"];
      const pe = values["evidence.period_end"];
      const g = (o, k) => GoldenSourceHelper.get(o, k);
      const read = async (from, to) =>
        (await fetchFigures(activeCompanyId, from, to)).map((r) => ({
          fieldPath: g(r, "FieldPath"), autoValue: g(r, "AutoValue"), manualValue: g(r, "ManualValue"),
          autoCodes: g(r, "AutoCodes"), autoLines: g(r, "AutoLineCount"),
        }));
      let rows = ps && pe ? await read(ps, pe) : [];
      let used = ps && pe ? `${ps} → ${pe}` : "";
      let filled = figuresToTopicValues(rows, topic.key);
      if (!Object.keys(filled).length && pe) {
        const y = String(pe).slice(0, 4);
        rows = await read(`${y}-01-01`, `${y}-12-31`);
        filled = figuresToTopicValues(rows, topic.key);
        used = `calendar year ${y}`;
      }
      const keys = Object.keys(filled);
      if (!keys.length) {
        setNotice("No ledger figures for this topic and period yet — map your account codes or enter the figures on Accounts & Ledger.");
        return;
      }
      const refFor = (k) => {
        const r = rows.find((x) => x.fieldPath === `${topic.key}.${k}`);
        if (r && r.manualValue !== null && r.manualValue !== undefined) return `Accounts & Ledger · entered figure (${used})`;
        return `Accounts & Ledger · codes ${r?.autoCodes || "?"} · ${r?.autoLines || 0} ledger lines (${used})`;
      };
      applyFilled(filled, "LEDGER", refFor);
      setValues((prev) => ({ ...prev, "evidence.source_system": prev["evidence.source_system"] || "Accounts & Ledger" }));
      setNotice(`Filled ${keys.length} field(s) from Accounts & Ledger (${used}): ${keys.map((k) => figureByPath(`${topic.key}.${k}`)?.label || k).join(", ")}.`);
    } catch (err) {
      setError(err?.message || "Could not read the ledger figures.");
    } finally {
      setLedgerBusy(false);
    }
  };

  // --- validation + save --------------------------------------------

  const missing = useMemo(
    () => (topic ? missingRequired(topic, values) : []),
    [topic, values],
  );

  const dirty = useMemo(() => {
    if (!baseline) return true;
    return JSON.stringify(baseline.values || {}) !== JSON.stringify(values);
  }, [baseline, values]);

  const period = { from: values["evidence.period_start"] || "", to: values["evidence.period_end"] || "" };

  /** Save to the report store. PRE_DRAFT stays pre-draft; DRAFT needs a reason and makes a version. */
  const save = async () => {
    if (!topic || activeCompanyId == null) return;
    if (!period.from || !period.to) {
      setError("Set the reporting period (start and end) in Evidence & assurance first.");
      return;
    }
    if (status === "DRAFT" && !reason.code) {
      setError("This section is a Draft: choose a reason for the change first.");
      return;
    }
    setSaving(true);
    setError("");
    setNotice("");
    try {
      // a different period is a different section — never overwrite another one blind
      const periodMoved = baseline && (baseline.from !== period.from || baseline.to !== period.to);
      if (!editingId || periodMoved) {
        const found = await findSection(activeCompanyId, topic.key, period.from, period.to);
        if (found?.sectionId && found.sectionId !== editingId) {
          setError(`A ${topic.name} section for ${period.from} → ${period.to} already exists — open it from the saved sections list.`);
          return;
        }
      }
      const payload = flattenFields(topic).map((f) => ({
        path: f.path,
        value: toStoredText(values[f.path]),
        unit: f.unit || null,
        sourceCode: sources[f.path]?.sourceCode || "MANUAL",
        sourceRef: sources[f.path]?.sourceRef || null,
      }));
      const res = await saveSection({
        companyId: activeCompanyId, topicKey: topic.key, pillarCode: topic.pillar, from: period.from, to: period.to,
        values: payload, reasonCode: status === "DRAFT" ? reason.code : null, reasonNote: status === "DRAFT" ? reason.note : null,
      });
      await loadSection(topic, res.sectionId);
      await refreshList();
      setNotice(
        res.changedCount === 0
          ? "Nothing had changed."
          : res.versionNo
            ? `Saved ${res.changedCount} change(s) as version ${res.versionNo}.`
            : `Saved ${res.changedCount} change(s). The section stays pre-draft until you set it to Draft.`,
      );
    } catch (err) {
      setError(err?.message || "Could not save the section.");
    } finally {
      setSaving(false);
    }
  };

  /** PRE_DRAFT → DRAFT (version 1). Unsaved edits must be saved first. */
  const setDraft = async () => {
    if (!editingId) return;
    if (dirty) {
      setError("Save your changes first, then set the section to Draft.");
      return;
    }
    const note = window.prompt(
      missing.length ? `${missing.length} required field(s) are still empty. Set to Draft anyway? Optional note:` : "Set this section to Draft? Optional note:",
      "",
    );
    if (note === null) return;
    setSaving(true);
    setError("");
    try {
      await setSectionDraft(editingId, note);
      await loadSection(topic, editingId);
      await refreshList();
      setNotice("Set to Draft as version 1. From now on every change needs a reason and makes a new version.");
    } catch (err) {
      setError(err?.message || "Could not set the section to Draft.");
    } finally {
      setSaving(false);
    }
  };

  /** Make an earlier version the current one. */
  const makeCurrent = async (versionNo) => {
    if (!editingId) return;
    const note = window.prompt(`Make version ${versionNo} the current version? Note (why):`, "");
    if (note === null) return;
    setSaving(true);
    setError("");
    try {
      await restoreSectionVersion(editingId, versionNo, note);
      await loadSection(topic, editingId);
      await refreshList();
      setNotice(`Version ${versionNo} is now the current version.`);
    } catch (err) {
      setError(err?.message || "Could not restore that version.");
    } finally {
      setSaving(false);
    }
  };

  return {
    // context
    activeCompanyId,
    activeCompanyName,
    hasCompany: activeCompanyId != null,
    companies,
    loadingCompanies,
    pickCompany,

    // phase
    phase,
    backToPick,

    // pillar
    pillars: PILLARS.map((p) => ({
      ...p,
      topicCount: REPORTING_TEMPLATES.filter((t) => t.pillar === p.key).length,
    })),
    pillar,
    pillarInfo: PILLARS.find((p) => p.key === pillar) || null,
    pickPillar,
    backToPillars,

    // picker
    templates: REPORTING_TEMPLATES.filter((t) => t.pillar === pillar).map((t) => ({
      key: t.key,
      name: t.name,
      pillar: t.pillar,
      topic: t.topic,
      summary: t.summary,
      blueprintRefs: t.blueprintRefs,
      guidance: t.guidance,
      groups: t.groups.map((g) => ({ key: g.key, label: g.label, fieldCount: g.fields.length })),
      groupCount: t.groups.length,
      fieldCount: fieldCount(t),
    })),
    submissions: pillar
      ? submissions.filter((s) => templateByKey(s.topicKey)?.pillar === pillar)
      : submissions,
    loadingList,
    listNotice,
    refreshList,
    startTopic,
    openSubmission,

    // form
    topic,
    values,
    sources,
    status,
    editingId,
    versions,
    currentVersionNo,
    draftedBy,
    history,
    reasonCodes,
    reason,
    setReason,
    setDraft,
    makeCurrent,
    missing,
    dirty,
    saving,
    error,
    notice,
    setValue,
    save,

    // HR & Workforce
    hrBusy,
    fillFromHr,

    // Accounts & Ledger
    ledgerBusy,
    fillFromLedger,

    // meter simulation
    simBatch,
    simBusy,
    simulateMeters,
    canPullMeters,
    pullFromMeters,
  };
}
